import { css } from '@emotion/react'
import { Cell, Grid, Heading, HFlow, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import { DateTime } from 'components/date'
import { Ellipsis } from 'components/Ellipsis'
import { HLabel } from 'components/HLabel'
import { Telefone } from 'components/label'
import { HideOnScreenSize } from 'components/responsive'
import { useAtendimentoHeaderCidadaoQuery } from 'graphql/hooks.generated'
import { Fragment } from 'react'
import { identidadeGeneroRecord, sexoRecord } from 'types/enums'
import { humanizeAge } from 'util/date/humanize-age'
import { CodigoIdentificadorCidadao } from 'view/cidadao/components/CodigoIdentificadorCidadao'

export interface AtendimentoHeaderCidadaoSectionProps {
  cidadaoId: ID
  dataAtendimento?: number
  showTelefoneAndUnidade: boolean
}

export const AtendimentoHeaderCidadaoSection = (props: AtendimentoHeaderCidadaoSectionProps) => {
  const { cidadaoId, dataAtendimento, showTelefoneAndUnidade } = props

  const { data: { cidadao } = {} } = useAtendimentoHeaderCidadaoQuery({
    variables: { cidadaoId, fetchCidadaoVinculacaoEquipeAndTelefones: showTelefoneAndUnidade },
  })

  const telefone = cidadao?.telefoneCelular || cidadao?.telefoneResidencial || cidadao?.telefoneContato

  if (!cidadao) return null

  return (
    <Grid gapVertical={0}>
      <Cell size={12}>
        <Grid alignItems='baseline' gap={0.5} gapVertical={0}>
          <Cell>
            <Heading level={2}>{(cidadao.nomeSocial ?? cidadao.nome).titleCase()}</Heading>
          </Cell>
          {cidadao.nomeSocial && (
            <Cell>
              <Text fontSize={1} fontWeight='bold'>
                (Nome social)
              </Text>
            </Cell>
          )}
          <Cell>
            <Text fontSize={0.9}>
              {cidadao.nomeSocial ? `${cidadao.nome.titleCase()} ` : ''}{' '}
              {cidadao.nomeTradicional && `|${cidadao.nomeTradicional.titleCase()}`}|&nbsp;
              {`Sexo ${sexoRecord[cidadao.sexo]}`}
            </Text>
            {cidadao.identidadeGeneroDbEnum && (
              <Text fontSize={0.9}>({identidadeGeneroRecord[cidadao.identidadeGeneroDbEnum]})</Text>
            )}
          </Cell>
          {cidadao.faleceu && (
            <Cell>
              <Tag style={styles.tag}>Óbito</Tag>
            </Cell>
          )}
          {!cidadao.ativo && (
            <Cell>
              <Tag style={styles.tag}>Inativo</Tag>
            </Cell>
          )}
          {cidadao.unificado && (
            <Cell>
              <Tag style={styles.tag}>Cadastro unificado</Tag>
            </Cell>
          )}
          {cidadao?.unificacaoBase && (
            <Cell>
              <Tooltip text={`Cadastro originado após unificação da Base ${cidadao.unificacaoBase}`}>
                <Tag style={styles.tag}>Base {cidadao.unificacaoBase.titleCase()}</Tag>
              </Tooltip>
            </Cell>
          )}
        </Grid>
      </Cell>

      <Cell size={12}>
        <Grid wrap={false}>
          <Cell>
            <VFlow vSpacing={0}>
              {cidadao.dataNascimento && (
                <Text
                  style={css`
                    white-space: nowrap;
                  `}
                >
                  {humanizeAge(cidadao.dataNascimento, dataAtendimento)}
                  {' | Nasceu em '}
                  <DateTime format='DD/MM/YYYY' value={cidadao.dataNascimento} />
                </Text>
              )}
              {showTelefoneAndUnidade && (
                <HLabel title='Telefone'>
                  <Telefone value={telefone} />
                </HLabel>
              )}
            </VFlow>
          </Cell>
          <Cell>
            <VFlow vSpacing={0}>
              <HFlow hSpacing={1}>
                <CodigoIdentificadorCidadao cpf={cidadao?.cpf} cns={cidadao?.cns} dataCyPrefix='CidadaoCabecalho' />
                <Ellipsis title={cidadao?.nomeMae?.titleCase()} maxLines={1} tooltipStyle={styles.tooltip}>
                  <HLabel title={<NomeMaeTitle />} data-cy='CidadaoCabecalho.nomeMae'>
                    {cidadao?.nomeMae?.titleCase()}
                  </HLabel>
                </Ellipsis>
              </HFlow>
              {showTelefoneAndUnidade && (
                <Ellipsis title={cidadao?.cidadaoVinculacaoEquipe?.unidadeSaude?.nome.titleCase()} maxLines={3}>
                  <HLabel title='Unidade responsável'>
                    {cidadao?.cidadaoVinculacaoEquipe?.unidadeSaude?.nome.titleCase()}
                  </HLabel>
                </Ellipsis>
              )}
            </VFlow>
          </Cell>
        </Grid>
      </Cell>
    </Grid>
  )
}

function NomeMaeTitle() {
  return (
    <Fragment>
      <HideOnScreenSize up='lg'>Mãe</HideOnScreenSize>
      <HideOnScreenSize down='lg'>Nome da mãe</HideOnScreenSize>
    </Fragment>
  )
}

const styles = {
  tag: css`
    &:not(:last-of-type) {
      margin-right: 0.5rem;
      margin-bottom: 0.5rem;
    }
  `,
  tooltip: css`
    white-space: normal;
    word-break: break-word;
  `,
}
